import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton, Chip, Alert, CircularProgress,
  Divider, MenuItem, FormControlLabel, Switch, Paper, Select, OutlinedInput, Checkbox, ListItemText, InputLabel, FormControl
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

let keyCounter = 0;
const nextKey = () => { keyCounter += 1; return `k${keyCounter}`; };

const toLocalQuestion = (q) => ({
  _key: nextKey(),
  id: q.id,
  text: q.text || '',
  type: q.type || 'text',
  variant: q.variant === 'long' ? 'long' : 'short',
  optionsText: Array.isArray(q.options) ? q.options.join('\n') : '',
  required: q.required === true,
  archived: q.archived === true,
  forInterviewerIds: Array.isArray(q.forInterviewerIds) ? q.forInterviewerIds.slice() : []
});

const toServerQuestion = (q) => {
  const out = {
    text: q.text, type: q.type, required: !!q.required, archived: !!q.archived,
    forInterviewerIds: Array.isArray(q.forInterviewerIds) ? q.forInterviewerIds : []
  };
  if (q.id) out.id = q.id;
  if (q.type === 'text') out.variant = q.variant;
  else out.options = String(q.optionsText || '').split('\n').map((x) => x.trim()).filter(Boolean);
  return out;
};

// Админ: интервьюеры опроса (добавить по email, скопировать личную ссылку, отключить)
// + дополнительные вопросы, ответы на которые видят только админ и интервьюер
export default function InterviewersDialog({ survey, open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [questions, setQuestions] = useState([]);
  const [usedIds, setUsedIds] = useState([]);
  const [qDirty, setQDirty] = useState(false);
  const [qSaving, setQSaving] = useState(false);

  const activeItems = items.filter((i) => i.active);
  const nameOf = (id) => (items.find((i) => i.userId === id)?.name) || id;

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    if (!survey) return;
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/surveys/${survey.id}/interviewers`, { headers: headers() });
      setItems(Array.isArray(data.interviewers) ? data.interviewers : []);
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось загрузить интервьюеров');
    } finally {
      setLoading(false);
    }
  }, [survey]);

  const loadQuestions = useCallback(async () => {
    if (!survey) return;
    try {
      const { data } = await axios.get(`${API_BASE}/surveys/${survey.id}/interviewers/questions`, { headers: headers() });
      setQuestions((Array.isArray(data.questions) ? data.questions : []).map(toLocalQuestion));
      setUsedIds(Array.isArray(data.usedQuestionIds) ? data.usedQuestionIds : []);
      setQDirty(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось загрузить дополнительные вопросы');
    }
  }, [survey]);

  const updateQ = (key, patch) => {
    setQuestions((prev) => prev.map((q) => (q._key === key ? { ...q, ...patch } : q)));
    setQDirty(true);
  };
  const addQ = () => {
    setQuestions((prev) => [...prev, toLocalQuestion({ type: 'text', variant: 'short' })]);
    setQDirty(true);
  };
  const moveQ = (idx, dir) => {
    setQuestions((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    setQDirty(true);
  };
  const removeQ = (key) => {
    setQuestions((prev) => prev.filter((q) => q._key !== key));
    setQDirty(true);
  };
  const saveQuestions = async () => {
    setError('');
    setInfo('');
    try {
      setQSaving(true);
      const { data } = await axios.put(
        `${API_BASE}/surveys/${survey.id}/interviewers/questions`,
        { questions: questions.map(toServerQuestion) },
        { headers: headers() }
      );
      setQuestions((Array.isArray(data.questions) ? data.questions : []).map(toLocalQuestion));
      setUsedIds(Array.isArray(data.usedQuestionIds) ? data.usedQuestionIds : []);
      setQDirty(false);
      setInfo('Дополнительные вопросы сохранены');
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось сохранить дополнительные вопросы');
    } finally {
      setQSaving(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadQuestions();
      setError('');
      setInfo('');
      setEmail('');
      load();
    }
  }, [open, load, loadQuestions]);

  const add = async () => {
    setError('');
    setInfo('');
    if (!email.trim()) return;
    try {
      const { data } = await axios.post(`${API_BASE}/surveys/${survey.id}/interviewers`, { email: email.trim() }, { headers: headers() });
      setEmail('');
      setInfo(data.alreadyAdded ? 'Этот интервьюер уже добавлен' : 'Интервьюер добавлен');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось добавить интервьюера');
    }
  };

  const disable = async (userId) => {
    if (!window.confirm('Отключить интервьюера? Его ссылка перестанет помечать ответы, уже собранные ответы останутся.')) return;
    try {
      await axios.delete(`${API_BASE}/surveys/${survey.id}/interviewers/${userId}`, { headers: headers() });
      // Сервер убрал интервьюера из адресатов вопросов: обновляем вопросы
      // (несохранённые правки не затираем, только убираем адресата локально)
      if (qDirty) {
        setQuestions((prev) => prev.map((q) => ({ ...q, forInterviewerIds: q.forInterviewerIds.filter((x) => x !== userId) })));
      } else {
        loadQuestions();
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось отключить интервьюера');
    }
  };

  const copy = (link) => {
    navigator.clipboard.writeText(link);
    setInfo('Ссылка скопирована');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Интервьюеры: {survey?.title}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Email аккаунта заказчика"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            sx={{ flex: 1, minWidth: 220 }}
          />
          <Button variant="contained" onClick={add} disabled={!email.trim()}>Добавить</Button>
        </Box>
        {loading ? <CircularProgress size={24} /> : items.length === 0 ? (
          <Typography color="text.secondary">Интервьюеров пока нет</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Интервьюер</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">Ответов</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.userId}>
                  <TableCell>{i.name || i.userId}</TableCell>
                  <TableCell>
                    <Chip size="small" label={i.active ? 'Активен' : 'Отключён'} color={i.active ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">{i.responseCount}</TableCell>
                  <TableCell align="right">
                    {i.link && (
                      <IconButton size="small" title="Скопировать личную ссылку" onClick={() => copy(i.link)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    )}
                    {i.active && (
                      <IconButton size="small" title="Отключить" onClick={() => disable(i.userId)}>
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Дополнительные вопросы для интервьюеров</Typography>
        <Alert severity="info" sx={{ my: 1.5 }}>
          Эти вопросы показываются только респондентам, которые проходят опрос по ссылке интервьюера, после основных страниц.
          Ответы видят только интервьюер (свои) и администратор. Заказчик опроса не увидит ни вопросы, ни ответы: ни в статистике, ни в Excel.
        </Alert>
        {questions.length === 0 && (
          <Typography color="text.secondary" sx={{ mb: 1 }}>Дополнительных вопросов нет</Typography>
        )}
        {questions.map((q, idx) => {
          const used = !!q.id && usedIds.includes(q.id);
          return (
            <Paper key={q._key} variant="outlined" sx={{ p: 1.5, mb: 1.5, opacity: q.archived ? 0.6 : 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small" label={`Вопрос ${idx + 1}`} value={q.text} sx={{ flex: 1, minWidth: 240 }}
                  onChange={(e) => updateQ(q._key, { text: e.target.value })}
                />
                <TextField
                  size="small" select label="Тип" value={q.type} sx={{ width: 170 }} disabled={used}
                  onChange={(e) => updateQ(q._key, { type: e.target.value })}
                >
                  <MenuItem value="text">Текст</MenuItem>
                  <MenuItem value="single">Один выбор</MenuItem>
                  <MenuItem value="multiple">Несколько выборов</MenuItem>
                </TextField>
                {q.type === 'text' && (
                  <TextField
                    size="small" select label="Поле" value={q.variant} sx={{ width: 140 }} disabled={used}
                    onChange={(e) => updateQ(q._key, { variant: e.target.value })}
                  >
                    <MenuItem value="short">Короткий</MenuItem>
                    <MenuItem value="long">Длинный</MenuItem>
                  </TextField>
                )}
              </Box>
              {q.type !== 'text' && (
                <TextField
                  fullWidth multiline minRows={2} size="small" sx={{ mt: 1 }} disabled={used}
                  label="Варианты (каждый с новой строки)" value={q.optionsText}
                  onChange={(e) => updateQ(q._key, { optionsText: e.target.value })}
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 280, flex: 1 }}>
                  <InputLabel id={`tg-${q._key}`}>Кому показывать</InputLabel>
                  <Select
                    labelId={`tg-${q._key}`} multiple displayEmpty
                    value={q.forInterviewerIds}
                    input={<OutlinedInput label="Кому показывать" />}
                    renderValue={(sel) => (sel.length === 0 ? 'Не назначен никому' : sel.map(nameOf).join(', '))}
                    onChange={(e) => updateQ(q._key, { forInterviewerIds: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
                  >
                    {activeItems.length === 0 && <MenuItem disabled value="">Нет активных интервьюеров</MenuItem>}
                    {activeItems.map((i) => (
                      <MenuItem key={i.userId} value={i.userId}>
                        <Checkbox size="small" checked={q.forInterviewerIds.includes(i.userId)} />
                        <ListItemText primary={i.name || i.userId} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button size="small" disabled={activeItems.length === 0}
                  onClick={() => updateQ(q._key, { forInterviewerIds: Array.from(new Set([...q.forInterviewerIds, ...activeItems.map((i) => i.userId)])) })}>
                  Всем интервьюерам
                </Button>
                {q.forInterviewerIds.length > 0 && !used && (
                  <Button size="small" color="inherit" onClick={() => updateQ(q._key, { forInterviewerIds: [] })}>Снять выбор</Button>
                )}
                {q.forInterviewerIds.length === 0 && <Chip size="small" color="warning" label="Не назначен никому" />}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Вопрос видят только выбранные интервьюеры. Отключённые интервьюеры в списке не показываются; при отключении интервьюер убирается из адресатов.
                {used && ' Есть ответы: адресатов можно только добавлять.'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={<Switch size="small" checked={q.required} onChange={(e) => updateQ(q._key, { required: e.target.checked })} />}
                  label="Обязательный"
                />
                {q.archived && <Chip size="small" label="В архиве" />}
                {used && <Typography variant="caption" color="text.secondary">Есть ответы: тип и варианты менять нельзя, удалить нельзя, можно архивировать</Typography>}
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" title="Выше" disabled={idx === 0} onClick={() => moveQ(idx, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                <IconButton size="small" title="Ниже" disabled={idx === questions.length - 1} onClick={() => moveQ(idx, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                <IconButton size="small" title={q.archived ? 'Вернуть из архива' : 'В архив'} onClick={() => updateQ(q._key, { archived: !q.archived })}>
                  {q.archived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                </IconButton>
                {!used && (
                  <IconButton size="small" title="Удалить" onClick={() => removeQ(q._key)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                )}
              </Box>
            </Paper>
          );
        })}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={addQ}>Добавить вопрос</Button>
          <Button variant="contained" onClick={saveQuestions} disabled={!qDirty || qSaving}>
            {qSaving ? 'Сохранение…' : 'Сохранить вопросы'}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}
